import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  Paper,
  TextField,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  Card,
  CardContent,
  Avatar,
  Alert,
  IconButton,
  useMediaQuery,
  Chip,
  InputAdornment,
  Collapse,
} from '@mui/material';
import {
  PersonOutline,
  EmailOutlined,
  PhoneAndroid,
  LocationCity,
  HomeOutlined,
  CommentOutlined,
  CheckCircle,
  Payment as PaymentIcon,
  PhotoCamera,
  Close as CloseIcon,
  LocalOffer as CouponIcon,
} from '@mui/icons-material';
import { sendOrder } from '../api';
import { useCart } from '../CartContext';
import { useCurrency } from '../CurrencyContext';
import {
  getPriceInCurrency,
  getCurrencyDecimals,
  convertDeliveryFee,
} from '../currencyHelpers';
import { useTranslation } from 'react-i18next';
import { getBranchWallets, submitTransaction } from '../api/walletApi';
import { validateCoupon } from '../couponApi';

// ── Business rules (match mobile) ──────────────────────────────
const MIN_ORDER_YER = 1000;

// ── Helpers (match mobile) ─────────────────────────────────────
const getBranchId = (b) => b?.Id ?? b?.id ?? null;
const getBranchName = (b) => b?.Name || b?.name || '';

const isCustomBundle = (item) =>
  item?.isCustom === true &&
  Array.isArray(item?.SubProducts) &&
  item.SubProducts.length > 0;

const isCpActive = (item) =>
  item?.CPAccountActive === true ||
  item?.CPAccountActive === 1 ||
  item?.CPAccountActive === 'true' ||
  item?.CPAccountActive === '1';

const CheckoutForm = ({
  deliveryFee,
  distance,
  hasLocations,
  onCouponApplied,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { cartItems, branchCarts, clearCart, selectedBranch } = useCart();
  const { selectedCurrency, getCurrencySymbol } = useCurrency();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [comment, setComment] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [availableWallets, setAvailableWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [paymentProof, setPaymentProof] = useState(null);
  const [paymentProofPreview, setPaymentProofPreview] = useState(null);

  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState('');

  const currencySymbol = getCurrencySymbol(selectedCurrency);
  const decimals = getCurrencyDecimals(selectedCurrency);

  // deliveryFee from page is already in YER → convert for display/order
  const convertedDeliveryFee = convertDeliveryFee(deliveryFee, selectedCurrency);

  // Build display groups for subtotal + payload
  const displayGroups = React.useMemo(() => {
    if (branchCarts && branchCarts.length > 0) {
      return branchCarts
        .map((g) => ({ ...g, items: Array.isArray(g?.items) ? g.items : [] }))
        .filter((g) => g.items.length > 0);
    }
    if (cartItems && cartItems.length > 0) {
      return [
        {
          branch: selectedBranch || { Id: null, Name: 'Cart' },
          items: cartItems,
        },
      ];
    }
    return [];
  }, [branchCarts, cartItems, selectedBranch]);

  // Flatten across all branches, attach branch info per item
  const allItems = React.useMemo(() => {
    const out = [];
    displayGroups.forEach((group) => {
      const bid = getBranchId(group.branch);
      const bname = getBranchName(group.branch);
      group.items.forEach((it) => {
        out.push({
          ...it,
          branchId: it.branchId ?? it.BranchId ?? bid,
          branchName: it.branchName ?? bname,
        });
      });
    });
    return out;
  }, [displayGroups]);

  // ── Subtotal (MIRRORS MOBILE 1:1) ─────────────────
  // CP active (=1)            → main price EXCLUDED (subs only)
  // CP inactive (0/null/etc.) → main price COUNTED (parent + subs)
  const subtotal = allItems.reduce((sum, item) => {
    const itemPrice = getPriceInCurrency(item, selectedCurrency);
    const cpActive = isCpActive(item);

    if (isCustomBundle(item)) {
      let subTotal = 0;
      item.SubProducts.forEach((sp) => {
        const spPrice = getPriceInCurrency(sp, selectedCurrency);
        const spQty = Number(sp.Quantity || 1);
        subTotal += spPrice * spQty;
      });
      // ✅ INVERTED RULE: CP active → exclude main; CP inactive → include main
      return (
        sum + ((cpActive ? 0 : itemPrice) + subTotal) * Number(item.quantity)
      );
    }

    if (item.isCustom && item.CustomTotal != null) {
      return sum + Number(item.CustomTotal) * Number(item.quantity);
    }

    return sum + itemPrice * Number(item.quantity);
  }, 0);

  // Minimum order check
  const minOrderInCurrency = convertDeliveryFee(MIN_ORDER_YER, selectedCurrency);
  const isBelowMinimum = subtotal > 0 && subtotal < minOrderInCurrency;

  const formatPrice = (amount) => {
    const num = Number(amount);
    if (isNaN(num)) return '0';
    return num.toFixed(decimals);
  };

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    const loadWallets = async () => {
      if (selectedBranch?.id) {
        try {
          const wallets = await getBranchWallets(selectedBranch.id);
          setAvailableWallets(wallets || []);
        } catch (err) {
          console.error('Failed to load wallets:', err);
        }
      }
    };
    loadWallets();
  }, [selectedBranch]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError(t('checkout.enterCouponCode', 'Please enter a coupon code'));
      return;
    }
    setCouponLoading(true);
    setCouponError('');
    setCouponResult(null);

    try {
      const result = await validateCoupon({
        code: couponCode.trim(),
        branchId: selectedBranch?.id,
        userId: user?.Id || user?.id,
        subTotal: subtotal,
        currency: selectedCurrency,
      });

      if (result.valid) {
        setCouponResult(result);
        setCouponError('');
        if (onCouponApplied) {
          onCouponApplied({
            code: result.code,
            couponId: result.couponId,
            discountAmount: result.discountAmount,
            formattedDiscount: result.formattedDiscountAmount,
          });
        }
      } else {
        setCouponError(
          result.message || t('checkout.invalidCoupon', 'Invalid coupon code')
        );
        setCouponResult(null);
        if (onCouponApplied) onCouponApplied(null);
      }
    } catch {
      setCouponError(
        t('checkout.couponValidationFailed', 'Failed to validate coupon')
      );
      setCouponResult(null);
      if (onCouponApplied) onCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponResult(null);
    setCouponError('');
    if (onCouponApplied) onCouponApplied(null);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(
          t('checkout.imageTooLarge', 'Image size must be less than 5MB')
        );
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPaymentProof(reader.result);
        setPaymentProofPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOrder = async () => {
    if (!hasLocations) {
      if (
        window.confirm(
          t('checkout.noLocation') + '\n' + t('checkout.addLocationMessage')
        )
      ) {
        navigate('/locations');
      }
      return;
    }

    if (isBelowMinimum) {
      setError(
        t(
          'checkout.minOrderWarning',
          `Minimum order is ${formatPrice(minOrderInCurrency)} ${currencySymbol}`
        )
      );
      return;
    }

    if (paymentMethod === 'wallet') {
      if (!selectedWallet) {
        setError(t('checkout.selectWallet', 'Please select a payment wallet'));
        return;
      }
      if (!paymentProof) {
        setError(
          t(
            'checkout.uploadPaymentProof',
            'Please upload payment proof screenshot'
          )
        );
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const discountAmount = couponResult?.discountAmount || 0;
      const totalAmount = Math.max(
        0,
        subtotal + convertedDeliveryFee - discountAmount
      );

      // Collect distinct source branch ids
      const sourceBranchIds = Array.from(
        new Set(
          allItems
            .map((it) => it.branchId)
            .filter((bid) => bid !== null && bid !== undefined)
        )
      );

      // ── Build order lines (MIRRORS MOBILE 1:1) ─────────────
      const orderLines = allItems.map((item) => {
        const itemPrice = getPriceInCurrency(item, selectedCurrency);
        const cpActive = isCpActive(item);
        const itemHasSubs = isCustomBundle(item);

        // Per-unit "main" price — INVERTED CP rule (matches mobile):
        //   • Bundle + CP active   → 0          (subs only)
        //   • Bundle + CP inactive → itemPrice  (parent counts)
        //   • Custom (CustomTotal) → CustomTotal
        //   • Regular              → itemPrice
        const mainPrice = itemHasSubs
          ? cpActive
            ? 0
            : itemPrice
          : item.isCustom && item.CustomTotal
          ? Number(item.CustomTotal)
          : itemPrice;

        // Build SubProducts array (used both as object & JSON string)
        const subProductsArray = itemHasSubs
          ? item.SubProducts.map((sp) => ({
              Id: sp.Id,
              Name: sp.Name || sp.ProductName,
              Description: sp.Description,
              Price: getPriceInCurrency(sp, selectedCurrency),
              Currency: selectedCurrency,
              CurrencyPrices: sp.CurrencyPrices,
              SizeType: sp.SizeType,
              Height: sp.Height,
              Width: sp.Width,
              ImageUrl: sp.ImageUrl,
              IsActive: sp.IsActive,
              Quantity: sp.Quantity
                ? typeof sp.Quantity === 'string'
                  ? parseInt(sp.Quantity)
                  : sp.Quantity
                : 1,
            }))
          : [];

        const base = {
          ProductCode:
            item.ProductCode?.toString() ||
            item.ProductId?.toString() ||
            item.Id?.toString() ||
            item.id?.toString() ||
            '',
          ProductId:
            item.ProductId?.toString() ||
            item.Id?.toString() ||
            item.id?.toString() ||
            '',
          ProductName: item.ProductName || item.name || '',
          ProductImage:
            item.imageJson ||
            item.ImageUrl ||
            item.Image ||
            item.ProductImage ||
            item.image ||
            '',
          Size: 1,
          Quantity: item.quantity,
          // ✅ Price as a NUMBER (mirrors mobile)
          Price: Number(mainPrice) || 0,
          Currency: selectedCurrency,
          CurrencyPrices: item.CurrencyPrices,
          SelectedColor: item.SelectedColor || null,
          SelectedSize: item.SelectedSize || null,
          BranchId: item.branchId ?? selectedBranch?.id ?? null,
          BranchName: item.branchName || getBranchName(selectedBranch),
        };

        if (itemHasSubs) {
          // Send subproducts in MULTIPLE shapes so the backend can save them
          // regardless of which property name it expects.
          const subProductsJson = JSON.stringify(subProductsArray);
          return {
            ...base,
            isCustom: true,
            IsCustom: true,
            CPAccountActive: cpActive,
            cpAccountActive: cpActive,
            CPAccountActiveValue: cpActive ? 1 : 0,
            SubProducts: subProductsArray,
            subProducts: subProductsArray,
            SubProductsJson: subProductsJson,
            subProductsJson: subProductsJson,
            CustomTotal: item.CustomTotal,
          };
        }

        // Custom-by-total (no subs)
        if (item.isCustom && item.CustomTotal != null) {
          return {
            ...base,
            isCustom: true,
            IsCustom: true,
            CustomTotal: item.CustomTotal,
          };
        }

        return base;
      });

      const orderPayload = {
        Order: orderLines,
        Customer: {
          Id: user?.Id || user?.id,
          Name: user?.FullName || user?.fullName || '',
          Email: user?.Email || user?.email || '',
          Phone: user?.Phone || user?.phone || '',
          Address: user?.Address || user?.address || '',
          City: user?.City || user?.city || '',
          Latitude: user?.Latitude,
          Longitude: user?.Longitude,
          Notes: '',
        },
        TotalAmount: totalAmount,
        SubTotal: subtotal,
        DeliveryFee: convertedDeliveryFee,
        Distance: distance || 0,
        Currency: selectedCurrency,
        BranchId: selectedBranch?.id,
        SourceBranchIds: sourceBranchIds,
        Comment: comment,
        CouponId: couponResult?.couponId || null,
        CouponCode: couponResult?.code || null,
        CouponDiscount: discountAmount,
      };

      // 🔍 Debug log
      console.log(
        '🧾 Order lines:',
        orderLines.map((l) => ({
          name: l.ProductName,
          price: l.Price,
          isCustom: l.isCustom,
          cpActive: l.CPAccountActive,
          subCount: Array.isArray(l.SubProducts) ? l.SubProducts.length : 0,
          jsonLen: l.SubProductsJson?.length || 0,
        }))
      );

      const response = await sendOrder(orderPayload);
      const backendOrderId =
        response?.OrderId ||
        response?.orderId ||
        response?.Content?.OrderId ||
        response?.Content?.orderId;

      if (!backendOrderId) {
        setError(t('checkout.orderError'));
        setLoading(false);
        return;
      }

      if (paymentMethod === 'wallet' && selectedWallet) {
        const transactionPayload = {
          orderId: backendOrderId,
          walletId: selectedWallet.Id,
          walletNumber: selectedWallet.WalletNumber,
          amount: totalAmount,
          currency: selectedCurrency,
          transactionImage: paymentProof,
          userId: user?.Id || user?.id,
        };
        try {
          await submitTransaction(transactionPayload);
        } catch {
          setError(
            t(
              'checkout.transactionSubmitWarning',
              'Order created but payment verification submission failed. Please contact support.'
            )
          );
        }
      }

      clearCart();

      if (paymentMethod === 'wallet') {
        navigate(`/prepayment-order/${backendOrderId}`);
      } else {
        navigate(`/order-status/${backendOrderId}`);
      }
    } catch (err) {
      console.error('Order error:', err);
      setError(t('checkout.failedOrder'));
    } finally {
      setLoading(false);
    }
  };

  const DetailItem = ({ icon, label, value }) => (
    <ListItem
      sx={{
        px: 0,
        py: { xs: 1.2, sm: 1.5 },
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 }, color: 'primary.main' }}>
        {React.cloneElement(icon, {
          sx: { fontSize: { xs: 20, sm: 22, md: 24 } },
        })}
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="600"
            sx={{
              textTransform: 'uppercase',
              fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
            }}
          >
            {label}
          </Typography>
        }
        secondary={
          <Typography
            variant="body1"
            color="text.primary"
            fontWeight="500"
            sx={{
              fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
              wordBreak: 'break-word',
            }}
          >
            {value || '-'}
          </Typography>
        }
      />
    </ListItem>
  );

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: 3, sm: 4 },
        width: '100%',
        bgcolor: 'background.paper',
        direction: i18n.dir(),
      }}
    >
      <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            mb: 1,
            fontFamily: 'Sarabun, sans-serif',
            fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.5rem' },
          }}
        >
          {t('checkout.personalDetails')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' } }}
        >
          {t(
            'checkout.detailsSubtitle',
            'Please review your delivery information below.'
          )}
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <List sx={{ p: 0, mb: { xs: 3, sm: 4 } }}>
        <DetailItem
          icon={<PersonOutline />}
          label={t('checkout.name')}
          value={user?.FullName || user?.fullName}
        />
        <DetailItem
          icon={<EmailOutlined />}
          label={t('checkout.email')}
          value={user?.Email || user?.email}
        />
        <DetailItem
          icon={<PhoneAndroid />}
          label={t('checkout.phone')}
          value={(user?.Phone || user?.phone || '')
            .toString()
            .replace(/^\+967/, '')
            .slice(-9)}
        />
        <DetailItem
          icon={<LocationCity />}
          label={t('register.city')}
          value={user?.City || user?.city}
        />
        <ListItem sx={{ px: 0, py: { xs: 1.2, sm: 1.5 } }}>
          <ListItemIcon
            sx={{ minWidth: { xs: 36, sm: 40 }, color: 'primary.main' }}
          >
            <HomeOutlined sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
                      </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="600"
                sx={{
                  textTransform: 'uppercase',
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                }}
              >
                {t('checkout.address')}
              </Typography>
            }
            secondary={
              <Typography
                variant="body1"
                color="text.primary"
                fontWeight="500"
                sx={{
                  fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                  wordBreak: 'break-word',
                }}
              >
                {user?.Address || user?.address}
              </Typography>
            }
          />
        </ListItem>
      </List>

      <Divider sx={{ my: { xs: 2.5, sm: 3 } }} />

      {/* Coupon Code Section */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: { xs: 1.5, sm: 2 },
            gap: 1,
          }}
        >
          <CouponIcon
            color="primary"
            sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }}
          />
          <Typography
            variant="h6"
            fontWeight="700"
            sx={{ fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' } }}
          >
            {t('checkout.couponCode', 'Coupon Code')}
          </Typography>
        </Box>

        {couponResult ? (
          <Card
            variant="outlined"
            sx={{
              border: 2,
              borderColor: 'success.main',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'success.dark' : 'success.50',
              borderRadius: { xs: 2, sm: 2.5 },
            }}
          >
            <CardContent
              sx={{
                py: { xs: 1.5, sm: 2 },
                px: { xs: 2, sm: 2.5 },
                '&:last-child': { pb: { xs: 1.5, sm: 2 } },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 1.5 },
                    flexWrap: 'wrap',
                  }}
                >
                  <CheckCircle
                    color="success"
                    sx={{ fontSize: { xs: 22, sm: 24 } }}
                  />
                  <Box>
                    <Typography
                      fontWeight="700"
                      sx={{
                        fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                      }}
                    >
                      {t('checkout.couponApplied', 'Coupon Applied!')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 0.3,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Chip
                        label={couponResult.code}
                        size="small"
                        color="success"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="success.main"
                        fontWeight="700"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                      >
                        -
                        {couponResult.formattedDiscountAmount ||
                          `${formatPrice(
                            couponResult.discountAmount
                          )} ${currencySymbol}`}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={handleRemoveCoupon}
                  startIcon={
                    <CloseIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  }
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    minWidth: 'auto',
                    px: { xs: 1.5, sm: 2 },
                  }}
                >
                  {t('checkout.removeCoupon', 'Remove')}
                </Button>
              </Box>
              {couponResult.description && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mt: 1,
                    display: 'block',
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  }}
                >
                  {couponResult.description}
                </Typography>
              )}
            </CardContent>
          </Card>
        ) : (
          <Box>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1, sm: 1.5 },
                alignItems: 'flex-start',
              }}
            >
              <TextField
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value.toUpperCase());
                  if (couponError) setCouponError('');
                }}
                placeholder={t('checkout.enterCoupon', 'Enter coupon code')}
                error={!!couponError}
                disabled={couponLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleApplyCoupon();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CouponIcon
                        color={couponError ? 'error' : 'action'}
                        sx={{ fontSize: { xs: 18, sm: 20 } }}
                      />
                    </InputAdornment>
                  ),
                  sx: {
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    borderRadius: { xs: 2, sm: 2.5 },
                    bgcolor: 'background.default',
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleApplyCoupon}
                disabled={couponLoading || !couponCode.trim()}
                size={isMobile ? 'medium' : 'large'}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: { xs: 2, sm: 2.5 },
                  px: { xs: 2.5, sm: 3 },
                  py: { xs: 1, sm: 1.2 },
                  minWidth: { xs: 80, sm: 100 },
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  whiteSpace: 'nowrap',
                }}
              >
                {couponLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t('checkout.apply', 'Apply')
                )}
              </Button>
            </Box>

            <Collapse in={!!couponError}>
              <Alert
                severity="error"
                sx={{
                  mt: 1.5,
                  borderRadius: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.8rem', sm: '0.85rem' },
                  py: { xs: 0.3, sm: 0.5 },
                }}
                onClose={() => setCouponError('')}
              >
                {couponError}
              </Alert>
            </Collapse>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mt: 1,
                display: 'block',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
              }}
            >
              {t(
                'checkout.couponHint',
                'Have a discount code? Enter it above to get a discount on your order.'
              )}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: { xs: 2.5, sm: 3 } }} />

      {/* Payment Method Section */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: { xs: 1.5, sm: 2 },
            gap: 1,
          }}
        >
          <PaymentIcon
            color="primary"
            sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }}
          />
          <Typography
            variant="h6"
            fontWeight="700"
            sx={{ fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' } }}
          >
            {t('checkout.paymentMethod', 'Payment Method')}
          </Typography>
        </Box>

        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            {/* Cash */}
            <Card
              variant="outlined"
              sx={{
                mb: 2,
                cursor: 'pointer',
                border: paymentMethod === 'cash' ? 2 : 1,
                borderColor:
                  paymentMethod === 'cash' ? 'primary.main' : 'divider',
                bgcolor:
                  paymentMethod === 'cash'
                    ? 'action.selected'
                    : 'background.paper',
                transition: 'all 0.2s',
                '&:active': {
                  transform: isMobile ? 'scale(0.98)' : 'none',
                },
              }}
              onClick={() => setPaymentMethod('cash')}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: { xs: 1.5, sm: 2 },
                  px: { xs: 2, sm: 2.5 },
                }}
              >
                <FormControlLabel
                  value="cash"
                  control={<Radio size={isMobile ? 'small' : 'medium'} />}
                  label={
                    <Box>
                      <Typography
                        fontWeight="700"
                        sx={{
                          fontSize: {
                            xs: '0.9rem',
                            sm: '1rem',
                            md: '1.05rem',
                          },
                        }}
                      >
                        {t('checkout.cashOnDelivery', 'Cash on Delivery')}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.8rem',
                            md: '0.85rem',
                          },
                        }}
                      >
                        {t(
                          'checkout.cashDescription',
                          'Pay with cash when you receive your order'
                        )}
                      </Typography>
                    </Box>
                  }
                  sx={{ flex: 1, m: 0 }}
                />
              </CardContent>
            </Card>

            {/* Wallet */}
            {availableWallets.length > 0 && (
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  border: paymentMethod === 'wallet' ? 2 : 1,
                  borderColor:
                    paymentMethod === 'wallet' ? 'primary.main' : 'divider',
                  bgcolor:
                    paymentMethod === 'wallet'
                      ? 'action.selected'
                      : 'background.paper',
                  transition: 'all 0.2s',
                  '&:active': {
                    transform: isMobile ? 'scale(0.98)' : 'none',
                  },
                }}
                onClick={() => setPaymentMethod('wallet')}
              >
                <CardContent
                  sx={{
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 2, sm: 2.5 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb:
                        paymentMethod === 'wallet'
                          ? { xs: 1.5, sm: 2 }
                          : 0,
                    }}
                  >
                    <FormControlLabel
                      value="wallet"
                      control={<Radio size={isMobile ? 'small' : 'medium'} />}
                      label={
                        <Box>
                          <Typography
                            fontWeight="700"
                            sx={{
                              fontSize: {
                                xs: '0.9rem',
                                sm: '1rem',
                                md: '1.05rem',
                              },
                            }}
                          >
                            {t('checkout.walletPayment', 'Wallet Payment')}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: {
                                xs: '0.75rem',
                                sm: '0.8rem',
                                md: '0.85rem',
                              },
                            }}
                          >
                            {t(
                              'checkout.walletDescription',
                              'Pay using mobile wallet and upload proof'
                            )}
                          </Typography>
                        </Box>
                      }
                      sx={{ flex: 1, m: 0 }}
                    />
                  </Box>

                  {paymentMethod === 'wallet' && (
                    <Box
                      sx={{
                        pl: { xs: 0, sm: 4 },
                        mt: { xs: 1.5, sm: 2 },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="700"
                        gutterBottom
                        sx={{
                          fontSize: {
                            xs: '0.85rem',
                            sm: '0.9rem',
                            md: '0.95rem',
                          },
                        }}
                      >
                        {t('checkout.selectWallet', 'Select Wallet')}:
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: { xs: 1.5, sm: 2 },
                          mb: { xs: 2, sm: 3 },
                        }}
                      >
                        {availableWallets.map((wallet) => (
                          <Card
                            key={wallet.Id}
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              border:
                                selectedWallet?.Id === wallet.Id ? 2 : 1,
                              borderColor:
                                selectedWallet?.Id === wallet.Id
                                  ? 'primary.main'
                                  : 'divider',
                              minWidth: { xs: 100, sm: 110, md: 120 },
                              textAlign: 'center',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: 'primary.main',
                                boxShadow: 2,
                              },
                              '&:active': { transform: 'scale(0.98)' },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedWallet(wallet);
                            }}
                          >
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                              {wallet.WalletImage && (
                                <Avatar
                                  src={wallet.WalletImage}
                                  variant="rounded"
                                  sx={{
                                    width: { xs: 60, sm: 70, md: 80 },
                                    height: { xs: 45, sm: 52, md: 60 },
                                    mx: 'auto',
                                    mb: { xs: 0.8, sm: 1 },
                                  }}
                                />
                              )}
                              <Typography
                                variant="body2"
                                fontWeight="700"
                                sx={{
                                  fontSize: {
                                    xs: '0.8rem',
                                    sm: '0.85rem',
                                    md: '0.9rem',
                                  },
                                }}
                              >
                                {wallet.WalletName}
                              </Typography>
                              {wallet.Description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    mt: 0.5,
                                    mb: 0.5,
                                    display: 'block',
                                    fontSize: {
                                      xs: '0.65rem',
                                      sm: '0.7rem',
                                      md: '0.75rem',
                                    },
                                  }}
                                >
                                  {wallet.Description}
                                </Typography>
                              )}
                              <Typography
                                variant="caption"
                                color="primary.main"
                                fontWeight="600"
                                sx={{
                                  display: 'block',
                                  fontSize: {
                                    xs: '0.7rem',
                                    sm: '0.75rem',
                                    md: '0.8rem',
                                  },
                                }}
                              >
                                {wallet.WalletNumber}
                              </Typography>
                              {wallet.WalletAccountName && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight="500"
                                  sx={{
                                    display: 'block',
                                    mt: 0.3,
                                    fontSize: {
                                      xs: '0.65rem',
                                      sm: '0.7rem',
                                      md: '0.75rem',
                                    },
                                  }}
                                >
                                  {wallet.WalletAccountName}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </Box>

                      {selectedWallet && (
                        <>
                          <Alert
                            severity="info"
                            sx={{
                              mb: { xs: 2, sm: 3 },
                              fontSize: {
                                xs: '0.8rem',
                                sm: '0.85rem',
                                md: '0.9rem',
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: 'inherit',
                                mb: selectedWallet.WalletAccountName
                                  ? 0.5
                                  : 0,
                              }}
                            >
                              {t('checkout.walletInstruction', 'Please send')}{' '}
                              <strong>
                                {formatPrice(
                                  Math.max(
                                    0,
                                    subtotal +
                                      convertedDeliveryFee -
                                      (couponResult?.discountAmount || 0)
                                  )
                                )}{' '}
                                {currencySymbol}
                              </strong>{' '}
                              {t('checkout.toWallet', 'to wallet')}{' '}
                              <strong>{selectedWallet.WalletNumber}</strong>
                            </Typography>
                            {selectedWallet.WalletAccountName && (
                              <Typography
                                variant="body2"
                                sx={{ fontSize: 'inherit', mt: 0.5 }}
                              >
                                {t('checkout.accountName', 'Account Name')}:{' '}
                                <strong>
                                  {selectedWallet.WalletAccountName}
                                </strong>
                              </Typography>
                            )}
                          </Alert>

                          <Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight="700"
                              gutterBottom
                              sx={{
                                fontSize: {
                                  xs: '0.85rem',
                                  sm: '0.9rem',
                                  md: '0.95rem',
                                },
                              }}
                            >
                              {t(
                                'checkout.uploadPaymentProof',
                                'Upload Payment Proof'
                              )}{' '}
                              <span
                                style={{ color: theme.palette.error.main }}
                              >
                                *
                              </span>
                            </Typography>
                            <Button
                              variant="outlined"
                              component="label"
                              startIcon={
                                <PhotoCamera
                                  sx={{ fontSize: { xs: 18, sm: 20 } }}
                                />
                              }
                              fullWidth
                              size={isMobile ? 'medium' : 'large'}
                              sx={{
                                mb: 2,
                                py: { xs: 1.2, sm: 1.5 },
                                borderStyle: 'dashed',
                                borderWidth: 2,
                                fontSize: {
                                  xs: '0.85rem',
                                  sm: '0.9rem',
                                  md: '0.95rem',
                                },
                              }}
                            >
                              {t('checkout.chooseImage', 'Choose Screenshot')}
                              <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={handleImageUpload}
                              />
                            </Button>
                            {paymentProofPreview && (
                              <Box sx={{ position: 'relative', mt: 2 }}>
                                <img
                                  src={paymentProofPreview}
                                  alt="Payment proof"
                                  style={{
                                    width: '100%',
                                    maxHeight: isMobile ? 300 : 400,
                                    objectFit: 'contain',
                                    borderRadius: 12,
                                    border: '2px solid #ddd',
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 6, sm: 8 },
                                    right: { xs: 6, sm: 8 },
                                    bgcolor: 'background.paper',
                                    boxShadow: 3,
                                    '&:hover': {
                                      bgcolor: 'error.light',
                                      color: 'white',
                                    },
                                    '&:active': { transform: 'scale(0.9)' },
                                  }}
                                  onClick={() => {
                                    setPaymentProof(null);
                                    setPaymentProofPreview(null);
                                  }}
                                >
                                  <CloseIcon
                                    sx={{ fontSize: { xs: 18, sm: 20 } }}
                                  />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </RadioGroup>
        </FormControl>
      </Box>

      <Divider sx={{ my: { xs: 2.5, sm: 3 } }} />

      {/* Order Note */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: { xs: 1, sm: 1.2 },
            gap: 1,
          }}
        >
          <CommentOutlined
            color="action"
            sx={{ fontSize: { xs: 18, sm: 20 } }}
                      />
          <Typography
            variant="subtitle2"
            fontWeight="700"
            sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
          >
            {t('checkout.commentNotice', 'Order Note')}
          </Typography>
        </Box>
        <TextField
          fullWidth
          multiline
          minRows={isMobile ? 2 : 3}
          maxRows={isMobile ? 4 : 6}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t(
            'checkout.commentPlaceholder',
            'Any special instructions?'
          )}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: { xs: 2, sm: 2.5, md: 3 },
              bgcolor: 'background.default',
              fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
            },
          }}
        />
      </Box>

      {/* Inline minimum-order warning above the button */}
      {isBelowMinimum && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: { xs: 1.5, sm: 2 },
            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
            fontWeight: 600,
          }}
        >
          {t(
            'checkout.minOrderWarning',
            `Minimum order is ${formatPrice(
              minOrderInCurrency
            )} ${currencySymbol}`
          )}
        </Alert>
      )}

      {/* Place Order Button */}
      <Button
        onClick={handleOrder}
        variant="contained"
        fullWidth
        size="large"
        disabled={loading || isBelowMinimum || allItems.length === 0}
        startIcon={
          loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <CheckCircle />
          )
        }
        sx={{
          py: { xs: 1.5, sm: 1.8, md: 2 },
          fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' },
          fontWeight: 700,
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          textTransform: 'none',
          boxShadow: theme.shadows[4],
          '&:hover': { boxShadow: theme.shadows[8] },
          '&:active': { transform: isMobile ? 'scale(0.98)' : 'none' },
        }}
      >
        {loading
          ? t('checkout.processing')
          : isBelowMinimum
          ? t(
              'checkout.minOrderButton',
              `Min ${formatPrice(minOrderInCurrency)} ${currencySymbol}`
            )
          : t('checkout.placeOrder')}
      </Button>
    </Paper>
  );
};

export default CheckoutForm;